import React from "react";
import { Box, Grid, Stack, Typography } from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import MonitorHeartRoundedIcon from "@mui/icons-material/MonitorHeartRounded";
import ErrorOutlineRoundedIcon from "@mui/icons-material/ErrorOutlineRounded";
import LayersRoundedIcon from "@mui/icons-material/LayersRounded";
import BarChartRoundedIcon from "@mui/icons-material/BarChartRounded";
import {
  useGetHealthQuery, useGetFailedJobsQuery, useRetryFailedJobMutation, useGetUsageSummaryQuery,
} from "../../../Api/platformApi";
import {
  AppPage, PageHeader, StatCard, StatusBadge, DataTable, ActionButton, type DataTableColumn,
} from "../../../Components/ui";
import { NAVY } from "../../../theme/tokens";

const SystemHealth: React.FC = () => {
  const { data: health, refetch, isFetching } = useGetHealthQuery(undefined, { pollingInterval: 30000 });
  const { data: failed } = useGetFailedJobsQuery(undefined, { pollingInterval: 30000 });
  const { data: usage } = useGetUsageSummaryQuery();
  const [retry] = useRetryFailedJobMutation();

  const components = health?.data ?? {};
  const failedJobs = failed?.data ?? [];
  const usageRows = usage?.data ?? [];

  const failedJobColumns: DataTableColumn<any>[] = [
    { id: "queue", label: "Queue", render: (j) => j.queue || "—" },
    {
      id: "error",
      label: "Error",
      render: (j) => (
        <Typography fontSize={13} sx={{ maxWidth: 420, wordBreak: "break-word" }}>
          {j.error}
        </Typography>
      ),
    },
    { id: "failed_at", label: "Failed At", render: (j) => j.failed_at || "—" },
  ];

  const usageColumns: DataTableColumn<any>[] = [
    { id: "period", label: "Period", render: (u) => u.period || "—" },
    { id: "company_id", label: "Company", render: (u) => u.company_id || "—" },
    {
      id: "channel",
      label: "Channel",
      render: (u) => <StatusBadge status={u.channel} />,
    },
    { id: "messages", label: "Messages", align: "right", render: (u) => u.messages },
    {
      id: "cost",
      label: "Est. Cost",
      align: "right",
      render: (u) => `$${Number(u.cost).toFixed(4)}`,
    },
  ];

  return (
    <AppPage
      disablePaper
      header={
        <PageHeader
          icon={<MonitorHeartRoundedIcon />}
          title="System Health"
          subtitle="Live platform status — refreshes every 30 seconds"
          actions={
            <ActionButton
              tone="navy"
              startIcon={<RefreshIcon />}
              onClick={() => refetch()}
              disabled={isFetching}
            >
              {isFetching ? "Refreshing…" : "Refresh"}
            </ActionButton>
          }
        />
      }
    >
      {/* Component health grid */}
      <Grid container spacing={2} mb={3}>
        {Object.entries(components).map(([key, c]: any) => (
          <Grid item xs={12} sm={6} md={4} key={key}>
            <StatCard
              icon={<LayersRoundedIcon />}
              label={key.replace(/_/g, " ")}
              value={
                <Stack direction="row" alignItems="center" spacing={1} mt={0.5}>
                  <StatusBadge status={c.status} />
                  {c.detail && (
                    <Typography fontSize={11} color="text.secondary" noWrap>
                      {c.detail}
                    </Typography>
                  )}
                </Stack>
              }
              color={
                ["online", "running", "healthy"].includes(c.status)
                  ? "#16A34A"
                  : c.status === "down"
                  ? "#EF4444"
                  : "#EA580C"
              }
            />
          </Grid>
        ))}

        {/* Summary stat cards */}
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<ErrorOutlineRoundedIcon />}
            label="Failed Jobs"
            value={failedJobs.length}
            color={failedJobs.length > 0 ? "#EF4444" : "#16A34A"}
          />
        </Grid>
        <Grid item xs={12} sm={6} md={4}>
          <StatCard
            icon={<BarChartRoundedIcon />}
            label="Usage Records"
            value={usageRows.length}
            color={NAVY}
          />
        </Grid>
      </Grid>

      {/* Failed Jobs */}
      <Box mb={3}>
        <Typography fontSize={15} fontWeight={700} color={NAVY} mb={1.5}>
          Failed Jobs
        </Typography>
        <DataTable
          columns={failedJobColumns}
          rows={failedJobs}
          getRowKey={(j) => j.id ?? j.uuid}
          actions={(j) => (
            <ActionButton
              tone="navy"
              size="small"
              onClick={() => retry(j.uuid)}
            >
              Retry
            </ActionButton>
          )}
          actionsLabel="Action"
          empty={{
            icon: <ErrorOutlineRoundedIcon />,
            title: "No failed jobs",
            message: "All queued jobs are healthy.",
          }}
        />
      </Box>

      {/* Usage summary */}
      <Box>
        <Typography fontSize={15} fontWeight={700} color={NAVY} mb={1.5}>
          Usage (billing foundation)
        </Typography>
        <DataTable
          columns={usageColumns}
          rows={usageRows}
          getRowKey={(u, i) => i}
          empty={{
            icon: <BarChartRoundedIcon />,
            title: "No usage yet",
            message: "Usage data will appear here once messages are sent.",
          }}
        />
      </Box>
    </AppPage>
  );
};

export default SystemHealth;
